// useEntityFieldEdit — canonical inline-edit handler for the
// `sectionContext.onFieldEdit` slot on `EntityViewPage`.
//
// Wraps `useUpdate` + `useInvalidate`, normalises empty-string → null
// (with an opt-out set for NOT-NULL columns), and invalidates the
// configured resources. The default invalidate target is the same as
// the write resource — override when reads come from a different
// resource than writes (e.g. a Postgres view like
// `transactions_with_checks` reading from `transactions`).
//
// Replaces both the hand-rolled `onFieldEdit` closures that previously
// lived in every entity's view-config hook AND the
// `EntityPatchProvider` + `useEntityPatch` + bridge-component dance
// that TransactionView used.

import { useCallback } from "react";
import { useInvalidate, useUpdate } from "@refinedev/core";
import { normalizeFieldEditValue } from "@ds/config/crud";

export interface UseEntityFieldEditOptions {
  /**
   * Field keys that must NOT be normalised from `""` to `null`. Use
   * for NOT-NULL columns where the form's `required` validation should
   * surface the empty value instead of the DB rejecting the patch.
   */
  nonNullableFields?: ReadonlySet<string>;
  /**
   * Refine resources to invalidate after each commit. Each entry has
   * both `"list"` and `"detail"` invalidated. Defaults to
   * `[writeResource]`. Pass an array when the read resource differs
   * from the write resource — e.g. `["transactions", "transactions_with_checks"]`
   * for a row written to the `transactions` table but rendered from a
   * read view that joins extra columns.
   */
  invalidateResources?: readonly string[];
  /**
   * Optional callback fired after a successful commit + invalidation.
   * Use for entity-specific refetch fan-out that Refine's cache layer
   * can't see — e.g. KPI bands bound to bespoke queries, workflow
   * provider `refetchAll()` registrations.
   */
  onSuccess?: () => void;
}

// Minimal interface for the EntityConfig overload — accepts any object
// that has `resource` and optional `writeResource` / `invalidateResources`.
// Typed loosely so callers can pass the entity config directly without
// pulling the entire EntityConfig<T> generic through.
export interface EntityConfigForEdit {
  resource: string;
  writeResource?: string;
  invalidateResources?: readonly string[];
}

/**
 * @param configOrWriteResource - Either an entity config (preferred — the
 *                                hook reads `writeResource`, falling back
 *                                to `resource`, and `invalidateResources`)
 *                                OR a bare write-resource string.
 * @param id                    - Current record id.
 * @param options               - Optional knobs: nonNullableFields,
 *                                invalidateResources (overrides config),
 *                                onSuccess.
 */
export function useEntityFieldEdit(
  configOrWriteResource: string | EntityConfigForEdit,
  id: number,
  options: UseEntityFieldEditOptions = {},
): (sectionKey: string, fieldKey: string, value: unknown) => Promise<void> {
  const { mutateAsync: updateMutation } = useUpdate();
  const invalidate = useInvalidate();
  const { nonNullableFields, invalidateResources: optInvalidateResources, onSuccess } = options;

  // Resolve writeResource + invalidateResources from the overload.
  const writeResource = typeof configOrWriteResource === "string"
    ? configOrWriteResource
    : (configOrWriteResource.writeResource ?? configOrWriteResource.resource);

  // Precedence: explicit options.invalidateResources > config.invalidateResources > [writeResource].
  const invalidateResources = optInvalidateResources
    ?? (typeof configOrWriteResource === "object"
      ? configOrWriteResource.invalidateResources
      : undefined);

  return useCallback(
    async (_sectionKey: string, fieldKey: string, value: unknown) => {
      const skipNullify = nonNullableFields?.has(fieldKey) ?? false;
      const normalized = normalizeFieldEditValue(value, skipNullify);
      await updateMutation({
        resource: writeResource,
        id,
        values: { [fieldKey]: normalized },
        successNotification: false,
      });
      // Refine's `invalidate({ invalidates: ["detail"] })` builds its query
      // key from `(resource, "one", id)`; passing `id` is REQUIRED or the
      // generated key is `[..., "one", ""]` which matches no real cache
      // entry. Refine's own `useUpdate` auto-invalidates the WRITE resource
      // (with the right id) — we explicitly invalidate any additional
      // resources here (e.g. read views).
      const resources = invalidateResources ?? [writeResource];
      await Promise.all(
        resources.map((r) =>
          invalidate({ resource: r, invalidates: ["list", "detail"], id }),
        ),
      );
      onSuccess?.();
    },
    [
      updateMutation, invalidate, writeResource, id,
      nonNullableFields, invalidateResources, onSuccess,
    ],
  );
}
